import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ClipboardList, Paperclip, Download, Eye } from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

// Duty Management Tab
export function DutyManagementTab(props) {
  var employees = props.employees;
  var [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState({});
  var [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  var [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  var [overview, setOverview] = useState([]);

  var eligibleEmployees = employees.filter(function(e) {
    return e.employee_type !== 'flex_employee';
  });

  useEffect(function() {
    fetchOverview();
  }, [selectedMonth, selectedYear]);

  function fetchOverview() {
    setLoading(true);
    fetch(API_URL + '/api/employee-admin/duty-overview?month=' + selectedMonth + '&year=' + selectedYear)
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setOverview(data.overview || []);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  function handleUpdateDuty(employeeId, date, status) {
    setSaving(Object.assign({}, saving, { [employeeId]: true }));
    fetch(API_URL + '/api/employee-admin/duty/' + employeeId, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: date, status: status })
    })
      .then(function(r) { return r.json(); })
      .then(function() {
        toast.success('Duty updated');
        setSaving(Object.assign({}, saving, { [employeeId]: false }));
        fetchOverview();
      })
      .catch(function() {
        toast.error('Failed to update');
        setSaving(Object.assign({}, saving, { [employeeId]: false }));
      });
  }

  function handleBulkMark(status) {
    var empIds = eligibleEmployees.map(function(e) { return e.id; });
    fetch(API_URL + '/api/employee-admin/duty/bulk-mark?date=' + selectedDate + '&status=' + status, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(empIds)
    })
      .then(function(r) { return r.json(); })
      .then(function() {
        toast.success('All marked as ' + status);
        fetchOverview();
      });
  }

  var statusColors = {
    'present': 'bg-green-500/20 text-green-300',
    'absent': 'bg-red-500/20 text-red-300',
    'half_day': 'bg-yellow-500/20 text-yellow-300',
    'leave': 'bg-purple-500/20 text-purple-300',
    'weekly_off': 'bg-blue-500/20 text-blue-300',
    'holiday': 'bg-pink-500/20 text-pink-300'
  };

  var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-white flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-green-400" />
            Duty Management
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={selectedMonth.toString()} onValueChange={function(v) { setSelectedMonth(parseInt(v)); }}>
              <SelectTrigger className="bg-slate-700/50 border-white/10 text-white w-28"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/10">
                {months.map(function(m, i) { return <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={function(v) { setSelectedYear(parseInt(v)); }}>
              <SelectTrigger className="bg-slate-700/50 border-white/10 text-white w-24"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-white/10">
                <SelectItem value="2024">2024</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-sm text-gray-400 mt-2">Track working days and off days for non-flex employees</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-4 bg-gradient-to-r from-green-900/30 to-blue-900/30 rounded-lg border border-green-500/30">
          <p className="text-white font-medium mb-3">Quick Mark for Date</p>
          <div className="flex items-center gap-3 flex-wrap">
            <Input type="date" value={selectedDate} onChange={function(e) { setSelectedDate(e.target.value); }} className="bg-slate-700/50 border-white/10 text-white w-40" />
            <Button size="sm" onClick={function() { handleBulkMark('present'); }} className="bg-green-600 hover:bg-green-700">All Present</Button>
            <Button size="sm" onClick={function() { handleBulkMark('weekly_off'); }} className="bg-blue-600 hover:bg-blue-700">All Weekly Off</Button>
            <Button size="sm" onClick={function() { handleBulkMark('holiday'); }} className="bg-pink-600 hover:bg-pink-700">All Holiday</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-gray-400 text-sm p-3">Employee</th>
                  <th className="text-center text-green-400 text-sm p-3">Present</th>
                  <th className="text-center text-red-400 text-sm p-3">Absent</th>
                  <th className="text-center text-yellow-400 text-sm p-3">Half Day</th>
                  <th className="text-center text-purple-400 text-sm p-3">Leave</th>
                  <th className="text-center text-blue-400 text-sm p-3">Weekly Off</th>
                  <th className="text-center text-pink-400 text-sm p-3">Holiday</th>
                  <th className="text-center text-white text-sm p-3">Working Days</th>
                  <th className="text-left text-gray-400 text-sm p-3">Mark Today</th>
                </tr>
              </thead>
              <tbody>
                {overview.map(function(emp) {
                  return (
                    <tr key={emp.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-3">
                        <p className="text-white font-medium">{emp.name}</p>
                        <p className="text-xs text-gray-400">{emp.department} • {emp.employee_type.replace(/_/g, ' ')}</p>
                      </td>
                      <td className="text-center p-3"><Badge className={statusColors.present}>{emp.present}</Badge></td>
                      <td className="text-center p-3"><Badge className={statusColors.absent}>{emp.absent}</Badge></td>
                      <td className="text-center p-3"><Badge className={statusColors.half_day}>{emp.half_day}</Badge></td>
                      <td className="text-center p-3"><Badge className={statusColors.leave}>{emp.leave}</Badge></td>
                      <td className="text-center p-3"><Badge className={statusColors.weekly_off}>{emp.weekly_off}</Badge></td>
                      <td className="text-center p-3"><Badge className={statusColors.holiday}>{emp.holiday}</Badge></td>
                      <td className="text-center p-3"><span className="text-white font-bold">{emp.total_working}</span></td>
                      <td className="p-3">
                        <Select onValueChange={function(empId) { return function(v) { handleUpdateDuty(empId, selectedDate, v); }; }(emp.id)}>
                          <SelectTrigger className="bg-slate-700/50 border-white/10 text-white w-32" disabled={saving[emp.id]}>
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-800 border-white/10">
                            <SelectItem value="present">Present</SelectItem>
                            <SelectItem value="absent">Absent</SelectItem>
                            <SelectItem value="half_day">Half Day</SelectItem>
                            <SelectItem value="leave">Leave</SelectItem>
                            <SelectItem value="weekly_off">Weekly Off</SelectItem>
                            <SelectItem value="holiday">Holiday</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {overview.length === 0 && !loading && (
          <p className="text-gray-400 text-center py-8">No non-flex employees found.</p>
        )}
      </CardContent>
    </Card>
  );
}

// Employee Files Admin Tab
export function EmployeeFilesAdminTab() {
  var [files, setFiles] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    fetchFiles();
  }, []);

  function fetchFiles() {
    setLoading(true);
    fetch(API_URL + '/api/employee-admin/all-employee-files')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        setFiles(data.files || []);
        setLoading(false);
      })
      .catch(function() { setLoading(false); });
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function getFileIcon(contentType, filename) {
    if (contentType && contentType.includes('pdf')) return '📄';
    if (contentType && contentType.includes('image')) return '🖼️';
    if (contentType && (contentType.includes('sheet') || contentType.includes('excel'))) return '📊';
    if (filename && filename.endsWith('.csv')) return '📊';
    return '📁';
  }

  var categoryBadges = {
    'work_report': { label: 'Work Report', color: 'bg-blue-500/20 text-blue-300' },
    'expense_receipt': { label: 'Expense', color: 'bg-green-500/20 text-green-300' },
    'vendor_visit': { label: 'Vendor/DP Visit', color: 'bg-amber-500/20 text-amber-300' },
    'other': { label: 'Other', color: 'bg-gray-500/20 text-gray-300' }
  };

  var typeBadges = {
    'fleet_manager': 'bg-blue-500/20 text-blue-300',
    'hr': 'bg-purple-500/20 text-purple-300',
    'operations': 'bg-teal-500/20 text-teal-300',
    'sales': 'bg-green-500/20 text-green-300',
    'marketing': 'bg-cyan-500/20 text-cyan-300'
  };

  return (
    <Card className="bg-slate-800/50 border-white/10">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Paperclip className="w-5 h-5 text-purple-400" />
          Employee Uploaded Files
        </CardTitle>
        <p className="text-sm text-gray-400">View and download files uploaded by employees</p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
          </div>
        ) : files.length > 0 ? (
          <div className="space-y-3">
            {files.map(function(f) {
              var catBadge = categoryBadges[f.file_category] || categoryBadges.other;
              var empBadge = typeBadges[f.employee_type] || 'bg-gray-500/20 text-gray-300';
              var hasVendorInfo = f.vendor_name || f.vendor_location || f.vendor_phone;
              return (
                <div key={f.id} className="p-4 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getFileIcon(f.content_type, f.filename)}</span>
                      <div>
                        <p className="text-white font-medium">{f.filename}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={empBadge}>{f.employee_name}</Badge>
                          <Badge className={catBadge.color}>{catBadge.label}</Badge>
                          <span className="text-xs text-gray-400">{f.department}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-400">{formatFileSize(f.file_size)}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">{new Date(f.uploaded_at).toLocaleDateString()}</span>
                        </div>
                        {f.description && <p className="text-xs text-gray-400 mt-1">{f.description}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <a href={f.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                        <Eye className="w-5 h-5" />
                      </a>
                      <a href={f.file_url} download className="text-green-400 hover:text-green-300">
                        <Download className="w-5 h-5" />
                      </a>
                    </div>
                  </div>
                  {hasVendorInfo && (
                    <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-4 text-sm">
                      {f.vendor_name && <span className="text-amber-300">📋 {f.vendor_name}</span>}
                      {f.vendor_location && <span className="text-gray-400">📍 {f.vendor_location}</span>}
                      {f.vendor_phone && <span className="text-gray-400">📞 {f.vendor_phone}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8">
            <Paperclip className="w-12 h-12 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400">No files uploaded by employees yet</p>
          </div>
        )}}
      </CardContent>
    </Card>
  );
}
